/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import Assert from "../../Utils/Assert";
import AppSnackbarItem from "./AppSnackbarItem";
import createAppSnackbarActions from "./createAppSnackbarActions";
import createAppSnackbarState from "./createAppSnackbarState";
import IAppSnackbar from "./IAppSnackbar";
import IAppSnackbarActions from "./IAppSnackbarActions";
import IAppSnackbarState from "./IAppSnackbarState";

export interface AppSnackbarContextProps {
    children: React.ReactNode;
}

const AppSnackbarActionsContext = React.createContext<IAppSnackbarActions | null>(null);
const AppSnackbarStateContext = React.createContext<IAppSnackbarState | null>(null);

// Die Aufteilung in State und Actions verhindert Rerender-Endloschleifen, wenn Actions in
// Effekten verwendet werden. Die Identität der Actions bleibt nun konstant, wenn sich der
// State der AppSnackbar ändert.
export function AppSnackbarProvider({ children }: AppSnackbarContextProps) {
    const [items, setItems] = React.useState<AppSnackbarItem[]>([]);
    const state = React.useMemo(() => createAppSnackbarState(items), [items]);
    const actions = React.useMemo(() => createAppSnackbarActions(setItems), [setItems]);
    return (
        <AppSnackbarStateContext.Provider value={state}>
            <AppSnackbarActionsContext.Provider value={actions}>
                {children}
            </AppSnackbarActionsContext.Provider>
        </AppSnackbarStateContext.Provider>
    );
}

export function useAppSnackbarState(): IAppSnackbarState {
    const error = "useAppSnackbarState kann nicht außerhalb des AppSnackbarProviders verwendet werden.";
    return Assert.notNull(React.useContext(AppSnackbarStateContext), error);
}

export function useAppSnackbarActions(): IAppSnackbarActions {
    const error = "useAppSnackbarActions kann nicht außerhalb des AppSnackbarProviders verwendet werden.";
    return Assert.notNull(React.useContext(AppSnackbarActionsContext), error);
}

// Hier darf keine Objektkopie (d.h. { ...obj }) verwendet werden, da sich dadurch die Identität
// der Funktionen ungewollt ändert und das UI gererendert wird.
export function useAppSnackbar(): IAppSnackbar {
    const {
        hasItems,
        items,
        itemsToArray,
        itemsToText,
        findItem,
    } = useAppSnackbarState();

    const {
        clear,
        clearAndKeepSuccess,
        clearByFieldPath,
        clearByPath,
        clearErrors,
        clearOnRouteChange,
        clearSuccessItems,
        push,
        pushInternalError,
        pushNetworkError,
        pushPdfError,
        pushResponseErrors,
        pushSuccess,
        pushCustomError,
    } = useAppSnackbarActions();

    return {
        hasItems,
        items,
        itemsToArray,
        itemsToText,
        findItem,
        clear,
        clearAndKeepSuccess,
        clearByFieldPath,
        clearByPath,
        clearErrors,
        clearOnRouteChange,
        clearSuccessItems,
        push,
        pushInternalError,
        pushNetworkError,
        pushPdfError,
        pushResponseErrors,
        pushSuccess,
        pushCustomError,
    };
}
